/**
 * EvalApiClient — SigV4-authenticated client for the Evaluation Service API.
 *
 * Auth chain:
 *   Federate id_token → Cognito Identity Pool → AWS STS temp credentials → SigV4 → API Gateway (AWS_IAM)
 *
 * Usage:
 *   import { evalApi } from "../client/EvalApiClient";
 *   const runs = await evalApi.listRuns();
 *   const detail = await evalApi.getRunDetail(requestId);
 */

import { SignatureV4 } from "@smithy/signature-v4";
import { HttpRequest } from "@smithy/protocol-http";
import { Sha256 } from "@aws-crypto/sha256-browser";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";
import { appConfig } from "../../config";
import { getIdToken } from "../../context/AuthContext";

// ─── Config ─────────────────────────────────────────────────────────────────

const EVAL_API_ENDPOINT = appConfig.evalApiEndpoint;

// ─── Types ──────────────────────────────────────────────────────────────────

export interface EvalRun {
  request_id: string;
  status: string;
  time_window_start: string;
  time_window_end: string;
  sessions: number;
  total_sessions?: number;
  unique_sessions?: number;
  total_evaluations: number;
  score: string;
  pass_rate: string;
  pass_threshold: string;
  metrics: string;
  config_ids: string[];
  created_at: string;
  finished_at: string;
  has_report: boolean;
}

export interface EvalSession {
  session_id: string;
  avg_score: number;
  status: string;
  evaluations_count: number;
  created_at: string;
}

export interface RunDetailResponse {
  run: EvalRun;
  sessions: EvalSession[];
}

export interface ReportUrlResponse {
  report_url: string;
  url?: string;
  config_id: string;
  expires_in: number;
  content_type?: string;
  s3_key?: string;
  all_reports?: Array<{ config_id: string; report_url: string; s3_key: string }>;
}

// ─── Core: SigV4 Signed Request ─────────────────────────────────────────────

async function signedRequest(method: string, path: string, body?: string): Promise<any> {
  // Step 1: Get Federate id_token
  const idToken = getIdToken();
  if (!idToken) {
    throw new Error("[EvalApiClient] Not authenticated — no id_token available");
  }

  // Step 2: Exchange id_token for AWS temporary credentials via Cognito Identity Pool
  const credentials = await fromCognitoIdentityPool({
    identityPoolId: appConfig.identityPoolId,
    logins: { [appConfig.identityProvider]: idToken },
    clientConfig: { region: appConfig.region },
  })();

  // Step 3: Sign the request with SigV4
  const signer = new SignatureV4({
    service: "execute-api",
    region: appConfig.region,
    credentials,
    sha256: Sha256,
  });

  const url = new URL(`${EVAL_API_ENDPOINT}${path}`);
  const query: Record<string, string> = {};
  url.searchParams.forEach((v, k) => {
    query[k] = v;
  });

  const headers: Record<string, string> = { host: url.hostname };
  if (body) {
    headers["content-type"] = "application/json";
  }
  // Forward the Federate id_token so the backend can identify the caller and
  // check ldap_groups (per-user / group authz). Set BEFORE signing so it is
  // covered by the SigV4 signature (tamper-proof) — mirrors IvtHubClient
  // (DAT-8256). SigV4 alone only proves some authenticated Cognito identity
  // (the shared authenticated role), not who.
  // NOTE: requires the eval API Gateway CORS Access-Control-Allow-Headers to
  // include x-id-token — ship the CDK/CORS change BEFORE this frontend change.
  headers["X-Id-Token"] = idToken;

  const request = new HttpRequest({
    method,
    protocol: url.protocol,
    hostname: url.hostname,
    path: url.pathname,
    query,
    headers,
    body: body || undefined,
  });

  const signed = await signer.sign(request);

  // Step 4: Send the signed request (strip host header — browsers forbid it)
  const fetchHeaders: Record<string, string> = {};
  for (const [key, value] of Object.entries(signed.headers)) {
    if (key.toLowerCase() !== "host") {
      fetchHeaders[key] = value;
    }
  }

  const qs = new URLSearchParams(signed.query as Record<string, string>).toString();
  const fetchUrl = qs ? `${url.origin}${signed.path}?${qs}` : `${url.origin}${signed.path}`;

  const response = await fetch(fetchUrl, {
    method,
    headers: fetchHeaders,
    body: body || undefined,
  });

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`[EvalApiClient] HTTP ${response.status}: ${text}`);
  }

  return response.json();
}

// ─── Public API Methods ─────────────────────────────────────────────────────

/**
 * `path` with `agent=<id>` added when an agent is given: the eval APIs then return
 * only that agent's data (no agent = every agent, as before).
 */
function withAgent(path: string, agent?: string): string {
  if (!agent) return path;
  return `${path}${path.includes("?") ? "&" : "?"}agent=${encodeURIComponent(agent)}`;
}

/**
 * Home "Online" stat cards for a period ("7d", "30d", ...), optionally for one agent.
 */
async function getHomeStats(period: string, agent?: string): Promise<any> {
  return signedRequest("GET", withAgent(`/home/stats?period=${encodeURIComponent(period)}`, agent));
}

/**
 * List evaluation runs with pagination.
 */
export interface ListRunsResponse {
  runs: EvalRun[];
  count: number;
  next_token: string | null;
}

async function listRuns(limit = 20, nextToken?: string, mode?: string, startDate?: string, endDate?: string, agent?: string): Promise<ListRunsResponse> {
  let path = "/runs?limit=" + limit;
  if (agent) path += "&agent=" + encodeURIComponent(agent);
  if (nextToken) path += "&next_token=" + encodeURIComponent(nextToken);
  if (mode) path += "&mode=" + encodeURIComponent(mode);
  if (startDate) path += "&start_date=" + encodeURIComponent(startDate + "T00:00:00Z");
  if (endDate) path += "&end_date=" + encodeURIComponent(endDate + "T23:59:59Z");
  const data = await signedRequest("GET", path);
  return {
    runs: data.runs || [],
    count: data.count || (data.runs?.length ?? 0),
    next_token: data.next_token || null,
  };
}

/**
 * Get detail for a specific run (includes sessions).
 */
async function getRunDetail(requestId: string): Promise<RunDetailResponse> {
  return signedRequest("GET", `/runs/${encodeURIComponent(requestId)}`);
}


/**
 * Get run metadata only (no sessions). Fast path for parallel loading.
 */
/**
 * Get session detail: evaluation scores + conversation messages.
 */
async function getSessionDetail(sessionId: string): Promise<any> {
  return signedRequest("GET", `/sessions/${encodeURIComponent(sessionId)}`);
}

async function getRunDetailOnly(requestId: string, agent?: string): Promise<any> {
  return signedRequest("GET", withAgent(`/runs/${encodeURIComponent(requestId)}/detail`, agent));
}
/**
 * Get sessions for a specific run.
 */
async function getRunSessions(requestId: string, agent?: string): Promise<EvalSession[]> {
  const data = await signedRequest("GET", withAgent(`/runs/${encodeURIComponent(requestId)}/sessions`, agent));
  return data.sessions || data;
}

/**
 * Get presigned URL for the HTML report of a run.
 */
async function getReportUrl(requestId: string): Promise<ReportUrlResponse> {
  return signedRequest("GET", `/runs/${encodeURIComponent(requestId)}/report`);
}

/**
 * Trigger a new evaluation run.
 */
async function triggerRun(params: {
  mode: string;
  config_ids?: string[];
  time_window_start?: string;
  time_window_end?: string;
}): Promise<{ request_id: string }> {
  return signedRequest("POST", "/runs", JSON.stringify(params));
}

// ─── Export as namespace ─────────────────────────────────────────────────────

export const evalApi = {
  getHistogram,
  getHomeStats,
  getRunEvaluations,
  fetchFailedCases,
  listRuns,
  getRunDetail,
  getRunDetailOnly,
  getSessionDetail,
  getRunSessions,
  getReportUrl,
  triggerRun,
  /** Low-level signed request for custom endpoints */
  signedRequest,
} as const;

export default evalApi;

// ─── Failed Cases ────────────────────────────────────────────────────────────

export interface FailedCase {
  evaluator: string;
  session_id: string;
  trace_id: string;
  span_id: string | null;
  score: number;
  evaluator_explanation: string;
  verdict: string;
  severity: string;
  root_cause: string;
  suggestion: string;
  action: string;
}

export interface FailedCasesResponse {
  failed_cases: FailedCase[];
  run_id: string | null;
  date: string;
  total: number;
}

export async function fetchFailedCases(date?: string, agent?: string): Promise<FailedCasesResponse> {
  const params = date ? `?date=${encodeURIComponent(date)}` : "";
  return signedRequest("GET", withAgent(`/failed-cases${params}`, agent));
}

// ─── Run Evaluations (flat list with optional LLM analysis) ──────────────────

export interface EvaluationAnalysis {
  verdict: string;
  agrees_with_evaluator: boolean;
  root_cause: string;
  suggestion: string;
  action: string;
  severity: string;
  llm_analysis: string;
}

export interface EvaluationRecord {
  evaluator: string;
  config_id: string;
  session_id: string;
  trace_id: string;
  span_id: string | null;
  level: string;
  score: number;
  label: string;
  explanation: string;
  timestamp_ms: number;
  service_name: string;
  evaluation_date: string;
  analysis: EvaluationAnalysis | null;
}

export interface RunEvaluationsResponse {
  request_id: string;
  generated_at: string | null;
  total_evaluations: number;
  total_sessions: number;
  pass_threshold: number;
  total_filtered: number;
  returned: number;
  offset: number;
  limit: number;
  filters_applied: Record<string, any>;
  evaluations: EvaluationRecord[];
  message?: string;
}

/**
 * The run's flat evaluations. ``hasAnalysis``: only the ones the LLM reviewed
 * (``?has_analysis=true``, the failure sample, so never cut by the server's limit).
 */
export async function getRunEvaluations(
  requestId: string,
  agent?: string,
  opts: { hasAnalysis?: boolean } = {},
): Promise<RunEvaluationsResponse> {
  const path = `/runs/${encodeURIComponent(requestId)}/evaluations${opts.hasAnalysis ? "?has_analysis=true" : ""}`;
  return signedRequest("GET", withAgent(path, agent));
}

// ─── Home Histogram ──────────────────────────────────────────────────────────

export interface HistogramDataPoint {
  date: string;
  evaluations: number;
  sessions: number;
}

export interface HistogramResponse {
  period: string;
  scope: string;
  days: number;
  total_days_with_data: number;
  data: HistogramDataPoint[];
}
export async function getHistogram(period = '30d', scope = 'all', startDate?: string, endDate?: string, agent?: string): Promise<HistogramResponse> {
  const params = new URLSearchParams({ period, scope });
  if (agent) params.set('agent', agent);
  if (startDate) params.set('start_date', startDate);
  if (endDate) params.set('end_date', endDate);
  return signedRequest('GET', `/home/histogram?${params.toString()}`);
}
